import { useState, useEffect } from "react";
import { useAuth } from "@/components/auth-provider";
import {
  addFavorite,
  removeFavorite,
  subscribeToFavorites,
} from "@/lib/favorites-service";
import { toast } from "sonner";

export function useFavorites() {
  const { user } = useAuth();
  const [favoriteIds, setFavoriteIds] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!user?.id) {
      setFavoriteIds([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    const unsubscribe = subscribeToFavorites(
      user.id,
      (ids) => {
        setFavoriteIds(ids);
        setIsLoading(false);
      },
      (error) => {
        console.error("Error loading favorites:", error);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }, [user?.id]);

  const toggleFavorite = async (propertyId: string) => {
    if (!user?.id) {
      toast.error("Please sign in to save favorites");
      return false;
    }

    const isFavorited = favoriteIds.includes(propertyId);

    try {
      if (isFavorited) {
        await removeFavorite(user.id, propertyId);
        toast.success("Removed from favorites");
      } else {
        await addFavorite(user.id, propertyId);
        toast.success("Added to favorites");
      }
      return true;
    } catch (error) {
      console.error("Error toggling favorite:", error);
      toast.error("Failed to update favorites");
      return false;
    }
  };

  const isFavorite = (propertyId: string): boolean => {
    return favoriteIds.includes(propertyId);
  };

  return {
    favoriteIds,
    isLoading,
    toggleFavorite,
    isFavorite,
    isAuthenticated: !!user,
  };
}
